import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, Subject, filter, takeUntil } from 'rxjs';
import { LoadingService } from './modules/shared/components/loading/loading.service';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  InteractionStatus,
  AccountInfo,
} from '@azure/msal-browser';
import { AuthService } from './modules/auth/services/auth.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  showNavbar = true;
  title = 'plt-transporte-ui';
  isLoading$: Observable<boolean> | null = null;
  tokenExpiration: string = '';
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private router: Router,
    private loadingService: LoadingService,
    private cdr: ChangeDetectorRef,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userAuthService: AuthService,
  ) {}

  ngOnInit() {
    this.isLoading$ = this.loadingService.isLoading$;
    localStorage.setItem('SystemName', 'SGTP');

    this.authService.instance
      .initialize()
      .then(() => {
        this.sanitizeAuthToken();
        this.setLoginDisplay();

        this.msalBroadcastService.inProgress$
          .pipe(
            filter(
              (status: InteractionStatus) => status === InteractionStatus.None,
            ),
            takeUntil(this._destroying$),
          )
          .subscribe(() => {
            this.checkAndSetActiveAccount();
            this.setLoginDisplay();
          });

        this.msalBroadcastService.msalSubject$
          .pipe(
            filter(
              (msg: EventMessage) =>
                msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
            ),
            takeUntil(this._destroying$),
          )
          .subscribe((msg) => {
            const payload = msg.payload as any;
            this.tokenExpiration = payload?.expiresOn;
            localStorage.setItem('tokenExpiration', this.tokenExpiration);
            if (payload?.account) {
              this.authService.instance.setActiveAccount(payload.account);
            }
            if (payload?.accessToken) {
              this.userAuthService.onLogin(payload.accessToken);
            }
          });
      })
      .catch((err) => {
        console.error('MSAL Initialization Failed', err);
      });

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.urlAfterRedirects || event.url;

        this.showNavbar =
          !(currentUrl === '/') &&
          !currentUrl.includes('auth/sign-up') &&
          !currentUrl.includes('auth/recover') &&
          !currentUrl.includes('recuperar-senha') &&
          !currentUrl.includes('redefinir-senha') &&
          !currentUrl.includes('auth/login');
      }
    });
  }

  ngAfterViewInit() {
    this.isLoading$?.subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount: AccountInfo | null =
      this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  private sanitizeAuthToken(): void {
    // Sobrescreve qualquer token stale (HS256 de pré-cadastro ou accessToken
    // de sessão anterior) com o idToken da conta MSAL ativa, imediatamente
    // após initialize(). Garante que getUserIdFromToken() e getToken() operem
    // com dados corretos antes de qualquer componente fazer requisições HTTP.
    let activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount) {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        activeAccount = accounts[0];
        this.authService.instance.setActiveAccount(activeAccount);
      }
    }
    if (activeAccount?.idToken) {
      this.userAuthService.onLogin(activeAccount.idToken);
      this.userAuthService.updateUserId();
    }
  }

  logout() {
    this.authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
